// IssueComposer — the tabbed entry composer at the foot of the drawer. The
// three tabs cover the day-to-day rhythm of an issue:
//   Comment        — free text → appended as a comment thread entry
//   Status update  — a transition-guarded target dropdown (only valid targets
//                    from the current status are offered, via
//                    allowedIssueTransitions) + an optional note → transition
//   Action         — description / party / optional owner / due date →
//                    action_raised
// The Post / Update status / Raise action submit buttons share the brand-violet
// btn-primary — the composer Post button is one of the tab's three sanctioned
// violet slots. Each submit no-ops on empty required fields so a stray click
// never fires a mutation.

import { useState } from "react";
import { allowedIssueTransitions } from "../canTransitionIssue";
import type {
  DeliverableOwner,
  IssueStatus,
} from "../../../../../types";
import type { AppendThreadActionInput } from "../../../../../types";

type ComposerTab = "comment" | "status" | "action";

const STATUS_LABEL: Record<IssueStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  awaiting_response: "Awaiting response",
  resolved: "Resolved",
  closed: "Closed",
};

const PARTY_OPTIONS: { value: DeliverableOwner; label: string }[] = [
  { value: "supplier", label: "Supplier" },
  { value: "customer", label: "Customer" },
  { value: "joint", label: "Joint" },
];

export interface IssueComposerProps {
  currentStatus: IssueStatus;
  onAddComment: (text: string) => void;
  /** The composer emits the target + optional note; the orchestrator folds
   *  in the issue's `version` for the optimistic-lock transition payload. */
  onTransition: (input: { to: IssueStatus; note?: string }) => void;
  onAddAction: (input: AppendThreadActionInput) => void;
  isSubmitting: boolean;
}

const TAB_CLASS = (active: boolean) =>
  `px-3 py-2 text-body-sm font-semibold border-b-2 -mb-px transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 ${
    active
      ? "border-brand-600 text-brand-700 dark:text-brand-400"
      : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400"
  }`;

export default function IssueComposer({
  currentStatus,
  onAddComment,
  onTransition,
  onAddAction,
  isSubmitting,
}: IssueComposerProps) {
  const [tab, setTab] = useState<ComposerTab>("comment");

  const [comment, setComment] = useState("");
  const [nextStatus, setNextStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [actionDescription, setActionDescription] = useState("");
  const [actionParty, setActionParty] = useState<DeliverableOwner>("supplier");
  const [actionDueDate, setActionDueDate] = useState("");

  const transitionTargets = allowedIssueTransitions(currentStatus);

  const submitComment = () => {
    const text = comment.trim();
    if (!text) return;
    onAddComment(text);
    setComment("");
  };

  const submitTransition = () => {
    if (!nextStatus) return;
    const note = statusNote.trim();
    onTransition({
      to: nextStatus as IssueStatus,
      ...(note ? { note } : {}),
    });
    setNextStatus("");
    setStatusNote("");
  };

  const submitAction = () => {
    const description = actionDescription.trim();
    if (!description || !actionDueDate) return;
    onAddAction({
      actionDescription: description,
      actionParty,
      actionDueDate,
    });
    setActionDescription("");
    setActionDueDate("");
  };

  return (
    <div data-testid="issue-composer" className="rounded-lg border border-slate-200 dark:border-slate-700">
      <div
        role="tablist"
        aria-label="Add to thread"
        className="flex gap-1 border-b border-slate-100 px-2 pt-1 dark:border-slate-700/60"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === "comment"}
          onClick={() => setTab("comment")}
          className={TAB_CLASS(tab === "comment")}
        >
          Comment
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "status"}
          onClick={() => setTab("status")}
          className={TAB_CLASS(tab === "status")}
        >
          Status update
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "action"}
          onClick={() => setTab("action")}
          className={TAB_CLASS(tab === "action")}
        >
          Action
        </button>
      </div>

      <div className="p-3">
        {tab === "comment" && (
          <div className="space-y-2">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment to the thread…"
              aria-label="Comment"
              rows={3}
              className="input w-full resize-y"
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={submitComment}
                disabled={isSubmitting}
                className="btn-primary"
              >
                Post comment
              </button>
            </div>
          </div>
        )}

        {tab === "status" && (
          <div className="space-y-2">
            <div>
              <label htmlFor="issue-composer-status" className="label">
                New status
              </label>
              <select
                id="issue-composer-status"
                value={nextStatus}
                onChange={(e) => setNextStatus(e.target.value)}
                className="input w-full"
              >
                <option value="">Select…</option>
                {transitionTargets.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              placeholder="Optional note…"
              aria-label="Status note"
              rows={2}
              className="input w-full resize-y"
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={submitTransition}
                disabled={isSubmitting || !nextStatus}
                className="btn-primary"
              >
                Update status
              </button>
            </div>
          </div>
        )}

        {tab === "action" && (
          <div className="space-y-2">
            <textarea
              value={actionDescription}
              onChange={(e) => setActionDescription(e.target.value)}
              placeholder="What needs doing…"
              aria-label="Action description"
              rows={2}
              className="input w-full resize-y"
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="issue-composer-party" className="label">
                  Party
                </label>
                <select
                  id="issue-composer-party"
                  value={actionParty}
                  onChange={(e) =>
                    setActionParty(e.target.value as DeliverableOwner)
                  }
                  className="input w-full"
                >
                  {PARTY_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="issue-composer-due" className="label">
                  Due date
                </label>
                <input
                  id="issue-composer-due"
                  type="date"
                  value={actionDueDate}
                  onChange={(e) => setActionDueDate(e.target.value)}
                  className="input w-full"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={submitAction}
                disabled={isSubmitting || !actionDescription.trim() || !actionDueDate}
                className="btn-primary"
              >
                Raise action
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
